import { apiFetch } from './client';

export type FolderType =
  | 'bulletin'
  | 'series'
  | 'rotation'
  | 'random'
  | 'playlist'
  | 'menu'
  | 'latest_n';

export interface RecordingFolderRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  ref_number: string;
  name: string;
  description: string | null;
  /** Typeless folders (created without a folder_type) have null here. */
  folder_type: FolderType | null;
  parent_folder_id: string | null;
  config: unknown;
  active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /**
   * SMS folder-notify (migration 0117). When notify_sms_list_id is set,
   * adding a new recording to this folder texts that subscriber list.
   * notify_sms_template supports {name} / {folder}; null = default text.
   */
  notify_sms_list_id: string | null;
  notify_sms_template: string | null;
  /**
   * Live count of non-deleted recordings whose folder_id points at
   * this folder. Populated by listFolders (the list endpoint) via a
   * correlated subquery; the single-folder GET endpoint may return it
   * as undefined.
   */
  recording_count?: string;
}

export interface RecordingFolderItemRow {
  id: string;
  folder_id: string;
  recording_id: string;
  reseller_id: string;
  customer_id: string;
  dtmf_key: string | null;
  sort_order: number;
  weight: number;
  /**
   * Scheduling window (migration 0111). UTC ISO-8601 strings, or null for
   * unbounded. When "now" is outside [available_from, available_until) the
   * call engine skips the item (not played, not counted) — but the portal
   * keeps showing it so it can be managed. NULL on either side = no bound.
   */
  available_from: string | null;
  available_until: string | null;
  created_at: string;
  recording_name: string;
  recording_duration_ms: number | null;
  recording_mime_type: string;
}

export interface CreateFolderBody {
  name: string;
  folder_type?: FolderType;
  description?: string | null;
  parent_folder_id?: string | null;
  config?: Record<string, unknown>;
  customer_id?: string;
}

export interface UpdateFolderBody {
  name?: string;
  description?: string | null;
  folder_type?: FolderType;
  parent_folder_id?: string | null;
  config?: Record<string, unknown>;
  active?: boolean;
  /** SMS folder-notify (migration 0117): null clears; omit to leave as-is. */
  notify_sms_list_id?: string | null;
  notify_sms_template?: string | null;
}

export interface AddItemBody {
  recording_id: string;
  dtmf_key?: string | null;
  sort_order?: number;
  weight?: number;
  /** UTC ISO-8601. null/omitted = unbounded (always available). */
  available_from?: string | null;
  available_until?: string | null;
}

export interface UpdateItemBody {
  dtmf_key?: string | null;
  sort_order?: number;
  weight?: number;
  /**
   * UTC ISO-8601. Pass null to clear a bound (back to unbounded). Omit a key
   * to leave that bound untouched — so you can change just the go-live date.
   */
  available_from?: string | null;
  available_until?: string | null;
}

export const recordingFolders = {
  list: (p: { folder_type?: FolderType; search?: string; limit?: number; customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.folder_type) q.set('folder_type', p.folder_type);
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<{ rows: RecordingFolderRow[] }>(`/api/recording-folders?${q}`);
  },
  get: (id: string) => apiFetch<RecordingFolderRow>(`/api/recording-folders/${id}`),
  create: (body: CreateFolderBody) =>
    apiFetch<RecordingFolderRow>('/api/recording-folders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: UpdateFolderBody) =>
    apiFetch<RecordingFolderRow>(`/api/recording-folders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/recording-folders/${id}`, { method: 'DELETE' }),

  listItems: (folderId: string) =>
    apiFetch<{ rows: RecordingFolderItemRow[] }>(`/api/recording-folders/${folderId}/items`),
  addItem: (folderId: string, body: AddItemBody) =>
    apiFetch<RecordingFolderItemRow>(`/api/recording-folders/${folderId}/items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateItem: (itemId: string, body: UpdateItemBody) =>
    apiFetch<RecordingFolderItemRow>(`/api/recording-folder-items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  removeItem: (itemId: string) =>
    apiFetch<void>(`/api/recording-folder-items/${itemId}`, { method: 'DELETE' }),
  /**
   * Replace the sort_order of a folder's items. Payload is the full ordered
   * list of item ids. Used by the bulletin drag-to-reorder UI.
   */
  reorderItems: (folderId: string, itemIds: string[]) =>
    apiFetch<void>(`/api/recording-folders/${folderId}/items/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ item_ids: itemIds }),
    }),
};

/** Hard cap on items per bulletin folder. Mirrors the backend constant. */
export const BULLETIN_MAX_ITEMS = 1000;

/** Max duration (milliseconds) of any single recording (1 hour). */
export const RECORDING_MAX_DURATION_MS = 3_600_000;
